import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { useSession } from "../../auth/AuthProvider";
import { addAdmin, removeUser } from "../../lib/operations";
import { formatDate } from "../../lib/licenses";
import { ConfirmDialog, PageHeader, SectionTitle } from "../../components/ui";
import { useAdminData } from "./AdminData";

export default function TeamPage() {
  const session = useSession();
  const { users, distributorById } = useAdminData();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [removing, setRemoving] = useState(null);

  const admins = users.filter((u) => u.role === "admin");
  const distributorLogins = users
    .filter((u) => u.role === "distributor")
    .sort((a, b) =>
      (distributorById[a.distributorId]?.name || "").localeCompare(
        distributorById[b.distributorId]?.name || "",
      ),
    );

  async function add(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await addAdmin({ email, actor: session.email });
      setEmail("");
    } catch (e) {
      setError(e.message || "Couldn't add that admin.");
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    setBusy(true);
    try {
      await removeUser({ email: removing, actor: session.email });
      setRemoving(null);
    } catch (e) {
      setError(e.message || "Couldn't remove access.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Access"
        title="Team"
        subtitle="Who can sign in. Admins see and manage everything; distributor logins only see the keys their company issued."
      />

      <SectionTitle>LiFE admins</SectionTitle>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Owner accounts always have access. They are set in firestore.rules,
          not here, so they aren't listed
          {session.owner ? " (you're signed in with one)" : ""}.
        </Typography>
        {admins.map((a) => (
          <Box
            key={a.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 0.75,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body2"
              sx={{ flex: 1, overflowWrap: "anywhere" }}
            >
              {a.id}
            </Typography>
            <Tooltip title="Remove admin">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setRemoving(a.id)}
                  disabled={a.id === session.email}
                  aria-label={`Remove ${a.id}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ))}
        <Box
          component="form"
          onSubmit={add}
          sx={{ display: "flex", gap: 1, mt: 2 }}
        >
          <TextField
            type="email"
            placeholder="colleague@tenses.asia"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ flex: 1 }}
            required
          />
          <Button
            type="submit"
            variant="outlined"
            color="inherit"
            disabled={busy || !email.trim()}
          >
            Add admin
          </Button>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <SectionTitle>Distributor logins</SectionTitle>
      {distributorLogins.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, color: "text.secondary" }}>
          No distributor logins yet. Add them from a distributor's page.
        </Paper>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Distributor</TableCell>
                  <TableCell sx={{ display: { xs: "none", sm: "table-cell" } }}>
                    Added
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distributorLogins.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell sx={{ overflowWrap: "anywhere" }}>
                      {u.id}
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/distributors/${u.distributorId}`}
                        underline="hover"
                      >
                        {distributorById[u.distributorId]?.name ||
                          u.distributorId}
                      </Link>
                    </TableCell>
                    <TableCell
                      sx={{ display: { xs: "none", sm: "table-cell" } }}
                    >
                      {formatDate(u.addedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}


      <ConfirmDialog
        open={!!removing}
        title="Remove admin?"
        body={`${removing} will no longer be able to sign in to KeyPortal.`}
        confirmLabel="Remove"
        destructive
        busy={busy}
        onConfirm={confirmRemove}
        onClose={() => setRemoving(null)}
      />
    </>
  );
}
